import {useEffect, useMemo, useState} from 'react';

import {ATTRIBUTE_EVENT_CODE, EventCreationMode} from 'src/modules/event-create/constants';
import {CreationAttribute, FieldName} from 'src/modules/event-create/typedefs';
import {getTemplate} from 'src/openapi/apis/EventTemplateControllerApi';
import {AttributeTemplate} from 'src/openapi/models/AttributeTemplate';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppDispatch} from 'src/store';

export const useTemplate = (
    mode: EventCreationMode,
    eventSave: EventChangeRequest,
    changeRequestUpdateId: number,
    eventId: number,
) => {
    const dispatch = useAppDispatch();
    const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);
    const [template, setTemplate] = useState<CreationAttribute[]>([]);
    const templateSource = useMemo(() => mode === EventCreationMode.update ? '' : eventSave.source,
        [eventSave.source, mode]);

    useEffect(() => {
        if (!eventSave.source && template.length) {
            setTemplate([]);
        }
    }, [eventSave.source, template.length, templateSource]);

    useEffect(() => {
        if (!template.length) {
            return;
        }

        const eventCodeAttribute = template.find(attribute => attribute.name === ATTRIBUTE_EVENT_CODE);
        const isEventCodeChanged =
            eventCodeAttribute && eventCodeAttribute[FieldName.POSSIBLE_VALUES][0] !== eventSave.code;

        if (isEventCodeChanged) {
            const updateEventCodeAttribute = template.map(attribute =>
                attribute.name === ATTRIBUTE_EVENT_CODE ? {...attribute, possibleValues: [eventSave.code]} : attribute);

            setTemplate(updateEventCodeAttribute);
        }
    }, [template, eventSave.code, mode, changeRequestUpdateId, eventId]);

    useEffect(() => {
        if (templateSource && changeRequestUpdateId || templateSource && !eventId && !changeRequestUpdateId) {
            setIsLoadingAttributes(true);

            const fetchData = async () => {
                const response =
                    await dispatch(getTemplate({eventSource: templateSource, eventType: eventSave.type}));

                if (response.payload.success) {
                    const attributesTemplate = response.payload.data.attributes as AttributeTemplate[];
                    const sortedAttributesTemplate = attributesTemplate.sort((a, b) => a.name.localeCompare(b.name));
                    setTemplate(sortedAttributesTemplate);
                }

                setIsLoadingAttributes(false);
            };
            fetchData();
            return;
        }
        setTemplate([]);
    }, [changeRequestUpdateId, dispatch, eventId, eventSave.type, templateSource]);

    return {isLoadingAttributes, template, setTemplate};
};
